import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";
import { THAI_VISA_TYPES, getThaiVisaType, type ThaiVisaType } from "@/content/visa-corpus/thai-visa-types";

function faqsTh(v: ThaiVisaType) {
  return [
    { q: `${v.titleTh} ใครยื่นได้?`, a: v.whoTh },
    { q: `อายุวีซ่ากับจำนวนวันที่อยู่ได้ต่างกันอย่างไร?`, a: `${v.validityTh} — ${v.stayTh}` },
    { q: `ต้องเตรียมเอกสารอะไรบ้าง?`, a: v.requirementsTh.join(" · ") },
    { q: `ขั้นตอนการยื่นเป็นอย่างไร?`, a: v.processTh.join(" → ") },
    { q: `ขออยู่ต่อได้อย่างไร?`, a: v.extensionTh },
    { q: `ข้อผิดพลาดที่ทำให้ถูกปฏิเสธคืออะไร?`, a: v.pitfallsTh.join(" · ") },
    { q: `หน่วยงานใดเป็นผู้พิจารณา?`, a: `${v.authorityTh} — การอนุมัติทุกกรณีเป็นดุลพินิจของพนักงานเจ้าหน้าที่ เราไม่รับประกันผลการพิจารณา` },
    { q: `ค่าบริการเท่าไหร่?`, a: "ค่าบริการขึ้นกับประเภทวีซ่า จำนวนเอกสาร และพื้นที่ยื่นเรื่อง สอบถามเจ้าหน้าที่ได้ทางโทรศัพท์ LINE หรืออีเมล เพื่อรับใบเสนอราคาตามกรณีของท่าน" },
  ];
}

export const Route = createFileRoute("/visa/thai/$type")({
  loader: ({ params }) => {
    const v = getThaiVisaType(params.type);
    if (!v) throw notFound();
    return { v };
  },
  component: ThaiVisaTypePageTH,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "ไม่พบประเภทวีซ่า" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { v } = loaderData;
    const title = `${v.titleTh} 2026 — เงื่อนไข เอกสาร และการอยู่ต่อ`;
    const description = `${v.titleTh} (${v.codeTh}) — คุณสมบัติผู้ยื่น เอกสารที่ต้องเตรียม ขั้นตอนยื่น การขออยู่ต่อ และข้อผิดพลาดที่พบบ่อย อ้างอิงสำนักงานตรวจคนเข้าเมืองและกระทรวงการต่างประเทศ`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/visa/thai/${params.type}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/visa/thai/${params.type}` }],
        keywords: [v.titleTh, v.codeTh, `${v.titleTh} เอกสาร`, `${v.titleTh} ขออยู่ต่อ`, "วีซ่าไทย 2026", "ตม.7"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "วีซ่าไทยทุกประเภท", url: `${site}/visa/thai` },
              { name: v.titleTh, url: `${site}/visa/thai/${params.type}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsTh(v))) },
        { type: "application/ld+json", children: JSON.stringify(buildHowToStepsSchema({ name: `ขั้นตอนการยื่น${v.titleTh}`, description: `ลำดับขั้นตอนการยื่น${v.titleTh} (${v.codeTh}) ตามแนวปฏิบัติของสำนักงานตรวจคนเข้าเมือง`, path: `/visa/thai/${params.type}`, steps: v.processTh, inLanguage: "th" })) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/visa/thai/${params.type}`)) },
      ],
    };
  },
});

function ThaiVisaTypePageTH() {
  const { v } = Route.useLoaderData();
  const faqs = faqsTh(v);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/visa/thai" className="hover:underline">วีซ่าไทยทุกประเภท</a> <span>/</span>{" "}
          <span>{v.titleTh}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{v.titleTh}</h1>
        <p className="mb-6 text-muted-foreground">{v.whoTh}</p>

        <div className="mb-8 grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border bg-card p-4 text-sm">
            <div className="mb-1 font-semibold">อายุวีซ่า</div>
            <p className="text-muted-foreground">{v.validityTh}</p>
          </div>
          <div className="rounded-lg border bg-card p-4 text-sm">
            <div className="mb-1 font-semibold">ระยะเวลาที่ได้รับอนุญาตให้อยู่</div>
            <p className="text-muted-foreground">{v.stayTh}</p>
          </div>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เอกสารและคุณสมบัติ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {v.requirementsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ขั้นตอนการยื่น</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            {v.processTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">การขออยู่ต่อในไทย</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{v.extensionTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ข้อผิดพลาดที่พบบ่อย</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {v.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — {v.titleTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">วีซ่าไทยประเภทอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {THAI_VISA_TYPES.filter((x) => x.slug !== v.slug).map((x) => (
              <a key={x.slug} href={`/visa/thai/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">ปรึกษาเจ้าหน้าที่</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            สอบถามเงื่อนไขและค่าบริการตามกรณีของท่านได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">ติดต่อเรา →</a>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          แหล่งอ้างอิง: {v.sources.map((s: { label: string })  => s.label).join(" · ")} — หลักเกณฑ์ ระยะเวลา และจำนวนวันที่ได้รับอนุญาตอาจเปลี่ยนแปลงตามประกาศ
          และการอนุมัติทุกกรณีเป็นดุลพินิจของพนักงานเจ้าหน้าที่ โปรดตรวจสอบกับหน่วยงานเจ้าของเรื่องก่อนดำเนินการ
        </p>

        <FaqSourceNote topic="visa" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}